import React, { Fragment, useEffect, useState, useRef } from "react";
import { Field, Form, FormikProvider, useFormik } from "formik";
import { ToastContainer, toast } from "react-toastify";
import * as Yup from "yup";
import { TabPane } from "reactstrap";
import classnames from "classnames";
import CustomSelect from "./CustomSelect";
import "react-toastify/dist/ReactToastify.css";
import FileUpload from "../../common/FileUpload";
import { useSelector, useDispatch } from "react-redux";
import { uploadSingleFileToS3 } from "../../api/fileUploadApi";

const initialValues = {
  department: "---",
  year: 2022,
  course_name: "",
  course_type: "Bachelors",
  start_date: "",
  course_duration: 4,
  course_id: "",
  class_of: "",
  created_at: "",
  updated_at: "",
  email_id: "",
  no_of_semesters: "",
  syllabus: "",
  category: "Self Funded",
  main_subjects: [],
  additional_subjects: [],
  course_fees: "",
  isGroupRequired: false,
  subjectGroups: []
};

const CreateClass = ({
  getAllSubjects,
  subjectList = [],
  createClass,
  creatingClass,
  permissions = {},
  isSuperAdmin,
  fetchingSubjects = false,
  customSubjectFee = 0,
  setCustomSubjectFee
}) => {
  useEffect(() => {
    getAllSubjects();
  }, []);
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileUploadRef = useRef(null);

  const validateSchema = Yup.object().shape({
    department: Yup.string().required("Department is required,"),
    year: Yup.number().required("Year is required."),
    course_name: Yup.string().required("Course Name is required."),
    syllabus: Yup.string().required("Syllabus is mandatory."),
    start_date: Yup.date().required("Start Date is mandatory."),
    course_duration: Yup.string().required("Course duration is mandatory."),
    course_id: Yup.string().required("Course Id is mandatory."),
    no_of_semesters: Yup.number().required("Mandatory"),
    course_fees: Yup.number().required("Course fees is Mandatory"),
  });

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: validateSchema,
    onSubmit: () => handleFormSubmit(),
  });

  const handleFormSubmit = () => {
    const payload = { ...values };
    payload.class_of = getClassValue();
    createClass(payload, apiCallback);
  };

  const apiCallback = (isSuccess) => {
    if (isSuccess) {
      notify();
      formik.resetForm();
      fileUploadRef.current()
    } else {
      notifyFailure();
    }
  };

  const notify = () =>
    toast.success(`Class created successfully.`, {
      position: "top-right",
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });

  const notifyFailure = () =>
    toast.error("Unable to create. Please try again....", {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
    });

  const { values = {} } = formik;
  const mainSubjects = subjectList.filter((subject) => {
    if (!values.additional_subjects.includes(subject.value)) {
      return 1;
    }

    return 0;
  });
  const additionalSubject = subjectList.filter((subject) => {
    if (!values.main_subjects.includes(subject.value)) {
      return 1;
    }
    return 0;
  });


  useEffect(() => {
    let fee = 0;
    const selectedSubjects = [...values.main_subjects, ...values.additional_subjects];
    selectedSubjects.forEach((subjectId) => {
      const found = subjectList.find(sub => sub.value === subjectId);
      if (found) {
        fee = fee + found?.subjectFees;
      }
    })
    setCustomSubjectFee(fee);
  }, [values.main_subjects, values.additional_subjects])

  const getClassValue = () => {
    const { start_date, course_duration } = values || {};
    if (start_date && course_duration) {
      const startDate = new Date(values.start_date);
      const year = startDate.getFullYear();
      const classOf = year + course_duration;
      return classOf;
    }

    return "-";
  };

  const updateFileUploadStatus = (newFiles, fieldName, uploadStatusObj) => {
    // const { uploading, uploadSuccess, uploadFailure } = obj;
    uploadStatusObj.uploading();
    if (newFiles) {
      const callbacks = {
        successCallback: syllabusUploadSuccessCallback,
        failureCallback: syllabusUploadFailureCallback,
      };
      dispatch(
        uploadSingleFileToS3(
          newFiles,
          fieldName,
          callbacks,
          uploadStatusObj
        )
      );
    }
  };

  const syllabusUploadSuccessCallback = (resp, fieldName, uploadStatusObj) => {
    const { link = "" } = resp;
    formik.setFieldValue("syllabus", link);
    uploadStatusObj.uploadSuccess();
  };

  const syllabusUploadFailureCallback = (resp, uploadStatusObj) => {
    toast.error("Error uploading syllabus, please try again!");
    uploadStatusObj.uploadFailure();
  };

  const onClickAddGroup = () => {
    const newGrp = {
      subjects: []
    };
    const grp = [...values.subjectGroups, newGrp];
    formik.setFieldValue("subjectGroups", grp);
  };

  const allSelectedSubjects = [...subjectList.filter(sub => values.main_subjects.includes(sub.value) || values.additional_subjects.includes(sub.value))];

  const getAllRemainingSubjects = (subjects = [], idx) => {
    const allGrpSub = [];
    values.subjectGroups.forEach(sub => allGrpSub.push(...sub.subjects));
    const alreadySelected = allSelectedSubjects.filter(sub => subjects.includes(sub.value));
    allGrpSub.flat(1);
    const remainingSub = allSelectedSubjects.filter((sub) => {
      if (!allGrpSub.includes(sub.value)) {
        return 1;
      }
      return 0;
    });
    remainingSub.push(...alreadySelected);

    return remainingSub;
  }

  const renderSubjectGroupSection = () => {
    return (
      <Fragment>
        <div className="col-sm-12">
          <div className="form-check">
            <Field
              name="isGroupRequired"
              className="form-check-input"
              type="checkbox"
              onChange={(e) => {
                formik.setFieldValue('isGroupRequired', e.target.checked);
                if (!e.target.checked) {
                  formik.setFieldValue('subjectGroups', []);
                }
              }}
            />
            <label className="ml-2">Do you want to create subject group(s)?</label>
          </div>
        </div>
        {values.isGroupRequired && <button className="btn btn-outline-primary btn-sm ml-1 mb-4" onClick={onClickAddGroup}>Add New Group</button>}
        {values.subjectGroups.length > 0 && values.isGroupRequired && (
          values.subjectGroups.map((grp, idx) => {
            const subList = getAllRemainingSubjects(grp.subjects, idx);
            return (
              <div key={idx} className="col-sm-12 d-flex align-items-center">
                <div className="grp-class">{`Group ${idx + 1}: `}</div>
                <div className="col-sm-10">
                  <div className="form-group">
                    <label>Provide Options Within Subjects:</label>
                    <Field
                      className="custom-select"
                      name={`subjectGroups[${idx}].subjects`}
                      options={subList}
                      component={CustomSelect}
                      placeholder="Select multiple Subjects"
                    />
                  </div>
                </div>
              </div>
            );
          })
        )}
      </Fragment>
    );
  };

  const renderForm = () => {
    return (
      <Form onSubmit={formik.handleSubmit} style={{ display: "contents" }}>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Department</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              as="select"
              name="department"
              className="form-control"
              value={values.department}
            >
              <option value="---">---</option>
              <option value="English">English</option>
              <option value="History">History</option>
              <option value="Political Science">Political Science</option>
              <option value="Economics">Economics</option>
              <option value="Sanskrit">Sanskrit</option>
              <option value="Hindi">Hindi</option>
              <option value="Urdu">Urdu</option>
              <option value="Philosophy">Philosophy</option>
              <option value="Geography">Geography</option>
              <option value="Sociology">Sociology</option>
              <option value="Education">Education</option>
              <option value="Psychology">Psychology</option>
              <option value="Law">LaW</option>
              <option value="Physical Education">Physical Education</option>
              <option value="Mathematics">Mathematics</option>
              <option value="Chemistry">Chemistry</option>
              <option value="Physics">Physics</option>
              <option value="Botany">Botany</option>
              <option value="Zoology">Zoology</option>
              <option value="Office and Library">Office and Library</option>
              <option value="Fourth Grade">Fourth Grade</option>
            </Field>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Year</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="year"
              className="form-control"
              placeholder="Year"
              type="number"
              value={values.year}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Course Name</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="course_name"
              className="form-control"
              placeholder="Course Name"
              type="text"
              value={values.course_name}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Course Type</label>
            <Field
              as="select"
              name="course_type"
              className="form-control"
              value={values.course_type}
            >
              <option value="Bachelors">Bachelors</option>
              <option value="Masters">Masters</option>
            </Field>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Course Id</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="course_id"
              className="form-control"
              placeholder="Course Id"
              type="text"
              value={values.course_id}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>{`Course Duration (Years)`}</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="course_duration"
              className="form-control"
              placeholder="Duration"
              type="number"
              value={values.course_duration}
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Category</label>
            <Field
              as="select"
              name="category"
              className="form-control"
              value={values.category}
            >
              <option value="Self Funded">Self Funded</option>
              <option value="Government Aided">Government Aided</option>
            </Field>
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Start Date</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="start_date"
              className="form-control"
              placeholder=""
              type="date"
              value={values.start_date}
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Department Email Id</label>
            <Field
              name="email_id"
              className="form-control"
              placeholder="cse@college.com"
              type="text"
              value={values.email_id}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Number of Semesters</label>
            <span style={{ color: "red" }}>*</span>
            <Field
              name="no_of_semesters"
              className="form-control"
              placeholder=""
              type="number"
              value={values.no_of_semesters}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Class of</label>
            <Field
              name="class_of"
              className="form-control"
              placeholder=""
              type="number"
              value={getClassValue()}
              disabled
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Created At</label>
            <Field
              name="created_at"
              className="form-control"
              placeholder=""
              type="date"
              value={values.created_at}
            />
          </div>
        </div>
        <div className="col-sm-3">
          <div className="form-group">
            <label>Updated At</label>
            <Field
              name="updated_at"
              className="form-control"
              placeholder=""
              type="date"
              value={values.updated_at}
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Main Subjects</label>
            <Field
              className="custom-select"
              name="main_subjects"
              options={mainSubjects}
              component={CustomSelect}
              placeholder="Select multiple Subjects"
            />
          </div>
        </div>
        <div className="col-sm-6">
          <div className="form-group">
            <label>Additional Subjects</label>
            <Field
              className="custom-select"
              name="additional_subjects"
              options={additionalSubject}
              component={CustomSelect}
              placeholder="Select multiple Subjects"
            />
          </div>
        </div>
        <div className="col-sm-12" style={{ display: "contents" }}>
          <div className="form-group col-sm-6">
            <label>Syllabus</label>
            <span style={{ color: "red" }}>*</span>
            <FileUpload
              fileUploadRef={fileUploadRef}
              fieldName="syllabus"
              setParentField={formik.setFieldValue}
              setSelectedFiles={setSelectedFiles}
              selectedFiles={selectedFiles}
              formik={formik}
              fileUploadStatus={updateFileUploadStatus}
            />
          </div>
          <div className="form-group col-sm-6">
            <label>Fees</label>
            <span style={{ color: "red" }}>*</span>
            <div className="d-flex align-items-center">
              <Field
                name="course_fees"
                className="form-control w-50"
                placeholder="Course fees"
                type="number"
                value={values.course_fees}
              />
              <div className="ml-2">{`+ ${customSubjectFee} INR`}<i className="fa fa-question-circle-o ml-2" aria-hidden="true" title="Additional fees is for some subjects."></i></div>
            </div>
            {(values.course_fees || customSubjectFee > 0) && <div className="mt-2">Total Fees: &#8377;&nbsp;{parseInt(values.course_fees, 10) + customSubjectFee}</div>}
          </div>
        </div>
        {renderSubjectGroupSection()}
        {renderFooterButtons()}
      </Form>
    );
  };

  const renderFooterButtons = () => {
    return (
      <div className="col-sm-12">
        <button
          type="submit"
          className="mr-3 btn btn-primary"
          disabled={(!isSuperAdmin && !permissions.create) || Object.keys(formik.errors).length || values.department === "---"}
        >
          {creatingClass ? <i className="fa fa-circle-o-notch fa-spin fa-fw"></i> : "Submit"}
        </button>
        <button
          type="button"
          onClick={() => formik.resetForm()}
          className="btn btn-outline-secondary btn-default"
        >
          Cancel
        </button>
      </div>
    );
  };

  return (
    <Fragment>
      <TabPane tabId={2} className={classnames(["fade show"])}>
        <div className="col-lg-12 col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Create Class</h3>
              {fetchingSubjects && (
                <span className="ml-3">
                  <i className="fa fa-circle-o-notch fa-spin fa-fw mr-2"></i>
                  <span>Fetching Subjects</span>
                </span>
              )}
            </div>
            <div className="card-body">
              <div className="row clearfix">
                <FormikProvider value={formik}>{renderForm()}</FormikProvider>
              </div>
            </div>
          </div>
        </div>
        <ToastContainer />
      </TabPane>
    </Fragment>
  );
};

export default CreateClass;
